/**
 * Write One-Step Equations generator (6.PAFR.1.1).
 *
 * Given a short word situation, choose the one-step equation that models it.
 * The correct equation is built with the equation-simple engine so its
 * displayed form is consistent, and distractors are the same equation with a
 * different operation or a swapped constant/result. The graded choice id is
 * always one of the presented options.
 *
 * Families:
 *   F1: addition situation      (x + b = c)
 *   F2: subtraction situation   (x - b = c)
 *   F3: multiplication situation (b * x = c)
 *   F4: division situation      (x / b = c)
 */

import { rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import {
  buildEquation,
  equationToLatex,
  type OneStepEquation,
  type OneStepOperation,
} from "../engine/equation-simple";
import type {
  Choice,
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "write-equations" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

const OP_FAMILY: Record<Family, OneStepOperation> = {
  F1: "add",
  F2: "subtract",
  F3: "multiply",
  F4: "divide",
};

interface Cfg {
  readonly families: readonly Family[];
  readonly operandMax: number;
  readonly solMax: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], operandMax: 9, solMax: 12 },
  standard: { families: ["F1", "F2", "F3", "F4"], operandMax: 10, solMax: 12 },
  hard: { families: ["F1", "F2", "F3", "F4"], operandMax: 12, solMax: 15 },
  challenge: { families: ["F3", "F4"], operandMax: 12, solMax: 15 },
};

const VARS = ["x", "n", "m"] as const;

/** Build the four candidate operations' equations for distractors. */
function equationFor(
  v: string,
  op: OneStepOperation,
  operand: ReturnType<typeof rat>,
  solution: ReturnType<typeof rat>,
): OneStepEquation {
  return buildEquation(v, op, operand, solution);
}

export class WriteEquationsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);
      const op = OP_FAMILY[family];
      const v = rng.pick(VARS);

      const operand = rat(rng.int(2, cfg.operandMax));
      const solution = rat(rng.int(2, cfg.solMax));
      const correctEq = equationFor(v, op, operand, solution);

      // Division problems: require an integer result for a clean equation.
      if (op === "divide" && !correctEq.result.isInteger()) continue;

      const b = operand.toString();
      const c = correctEq.result.toString();

      let story: string;
      switch (family) {
        case "F1":
          story = `A number ${v} increased by ${b} equals ${c}. Which equation models this?`;
          break;
        case "F2":
          story = `A number ${v} decreased by ${b} equals ${c}. Which equation models this?`;
          break;
        case "F3":
          story = `${b} times a number ${v} equals ${c}. Which equation models this?`;
          break;
        case "F4":
          story = `A number ${v} divided by ${b} equals ${c}. Which equation models this?`;
          break;
      }

      // Build one candidate per operation (correct + 3 distractors), unique.
      const ops: OneStepOperation[] = ["add", "subtract", "multiply", "divide"];
      const seen = new Set<string>();
      const candidates: { latex: string; correct: boolean }[] = [];
      for (const candOp of ops) {
        const eq = equationFor(v, candOp, operand, solution);
        // Keep every distractor's displayed result equal to the correct one so
        // the choice differs only in the operation, isolating the concept.
        const latex = equationToLatex({ ...eq, result: correctEq.result });
        if (seen.has(latex)) continue;
        seen.add(latex);
        candidates.push({ latex, correct: candOp === op });
      }

      const withIds = candidates.map((c2, i) => ({ id: `c${i}`, ...c2 }));
      const shuffled = rng.shuffle(withIds);
      const choices: Choice[] = shuffled.map((o) => ({ id: o.id, latex: o.latex }));
      const correct = shuffled.find((o) => o.correct);
      if (!correct) continue;

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: story },
        answerType: "choice",
        answer: { kind: "choice", value: correct.id },
        steps: [
          {
            description: "Translate the words into an operation on the variable, then set it equal to the result.",
            latex: equationToLatex({ ...correctEq, result: correctEq.result }),
          },
        ],
        hints: [
          { level: 1, text: "Identify the operation: 'increased by' adds, 'times' multiplies, 'divided by' divides." },
          { level: 2, text: "The unknown is the variable; the result goes on the other side of the equals sign." },
        ],
        subskills: topic.subskills,
        choices,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "choice" ? q.answer.value : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const writeEquations = new WriteEquationsGenerator();
